import {
  getGroupData,
  getGroupName,
  getPoaIndex,
  getSelectedPeriod,
} from "../../../../services/GroupContext";
import { addSquareBorder } from "../../../../utils/addSquareBorder";
import { processDateJS } from "../../../../utils/dateProcessor";
import { get_relevant_periods } from "../../../../utils/PeriodUtils";
import {
  adjustments_potentialVariables,
  AP_potentialVariables,
  calculationKeys,
  cir_potentialVariables,
  company_potentialVariables,
  cte_potentialVariables,
  keyExceptionsMap,
  ntdc_potentialVariables,
  ntlr_potentialVariables,
  ofa_potentialVariables,
  reliefs_potentialVariables,
  tdc_potentialVariables,
  tlr_potentialVariables,
} from "./metrics";

export const getRelevantGroupCompanies = () => {
  const savedData = getGroupData();
  const poaData = savedData?.periods_of_account?.[getPoaIndex()];

  const relevantGroupCompanies = poaData?.adjusted_caps?.map(
    (adjustedCap) => adjustedCap.company_ID,
  );
  const companies = savedData?.companies?.filter((company) =>
    relevantGroupCompanies?.includes(company.company_ID),
  );

  companies?.sort((a, b) => {
    const nameA = a.company_name.toLowerCase();
    const nameB = b.company_name.toLowerCase();
    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;
    return 0;
  });

  return companies;
};

//JCW TO-DO - Discuss with Katya / Dev Team, but this is referencing company_net_tax_interest_expense_income_gbp, which I think is the sum across all accounting periods, so doesn't need to be summed. We are in effect double counting this now.
export const getCompanyNtiei = (company_ID) => {
  const savedData = getGroupData();
  const poaData = savedData?.periods_of_account?.[getPoaIndex()];

  let ntieiTotal = 0;
  for (let entry in poaData?.adjusted_caps) {
    let cap = poaData?.adjusted_caps[entry];
    if (cap && cap.company_ID === company_ID) {
      if (cap.company_net_tax_interest_expense_income_gbp) {
        // looks like company_net_tax_interest_expense_income_gbp is not populated for any of the groups I tested on - to be checked
        ntieiTotal = cap.company_net_tax_interest_expense_income_gbp;
      }
    }
  }

  return ntieiTotal;
};

export const getCompanyTaxEbitda = (company_ID) => {
  const savedData = getGroupData();
  const poaData = savedData?.periods_of_account?.[getPoaIndex()];

  let ebitdaTotal = 0;
  for (let entry in poaData?.adjusted_caps) {
    let cap = poaData?.adjusted_caps[entry];
    if (cap && cap.company_ID === company_ID) {
      if (cap.tax_ebitda_gbp) {
        if (isNaN(cap.tax_ebitda_gbp)) {
          ebitdaTotal += cap.tax_ebitda_gbp["value"];
        } else {
          ebitdaTotal += cap.tax_ebitda_gbp;
        }
      }
    }
  }

  return ebitdaTotal;
};

export const copyrightMessage =
  "©" +
  new Date().getFullYear() +
  " PricewaterhouseCoopers LLP. All rights reserved. ‘PwC’ refers to the UK member firm, and may sometimes refer to the PwC network. Each member firm is a separate legal entity. Please see www.pwc.com/structure for further details.";

export const formatDate = (date) => {
  return processDateJS(date)?.format("DD/MM/YYYY");
};

const getAngieOrqngieEDC = (data) => {
  let angieorqngieEDC = "ANGIE or QNGIE";
  if (data.ratio_type == "Group Ratio") {
    angieorqngieEDC = data.qualifying_net_group_interest_expense;
  } else if (data.ratio_type == "Fixed Ratio") {
    angieorqngieEDC = data.adjusted_net_group_interest_expense;
  } else {
    angieorqngieEDC = 0;
  }

  return angieorqngieEDC;
};

const handleNaNs = (value) => (isNaN(value) ? 0 : value);

const calculateMetric = (key, data) => {
  if (
    [
      "APnet_tax_interest_totals",
      "APs407_tiopa_net_tax_interest_expense",
    ].includes(key)
  ) {
    return (
      data.ofa_subtotal +
      data.tdc_subtotal +
      data.tlr_subtotal +
      data.ntdc_subtotal +
      data.ntlr_subtotal
    );
  } else if (key === "current_yr_interest") {
    const current_year_interest_allowance =
      data.aggregate_net_tax_interest_expense + data.group_reactivations;
    return current_year_interest_allowance;
  } else if (key === "unused_interest_gen") {
    const current_year_interest_allowance =
      data.aggregate_net_tax_interest_expense + data.group_reactivations;
    const unused_interest_allowance_generated_curr_period =
      data.group_interest_allowance - current_year_interest_allowance;

    return unused_interest_allowance_generated_curr_period;
  } else if (key === "angieorqngieEDC") {
    return getAngieOrqngieEDC(data);
  } else if (key === "group_percentage_tax_ebitdaEDC") {
    return -1 * data.aggregate_tax_ebitda * data.group_percentage;
  } else if (key === "excess_debt_cap_generated_pre_cflimitEDC") {
    return Math.max(
      data.group_debt_cap - data.aggregate_tax_ebitda * data.group_percentage,
      0,
    );
  } else if (key === "cflimitEDC") {
    return data.excess_debt_cap_from_prior_period + data.group_disallowance;
  } else if (key === "fixed_ratio") {
    return data.aggregate_tax_ebitda * 0.3;
  } else if (key === "group_ratio_ebitda") {
    return data.gr_percentage * data.aggregate_tax_ebitda;
  } else if (key === "tax_interest_total_for_period_gbp") {
    const total =
      data.ofa_subtotal +
      data.tdc_subtotal +
      data.tlr_subtotal +
      data.ntdc_subtotal +
      data.ntlr_subtotal;
    return total / data.fx_rate;
  } else if (key === "tax_ebitda_total_for_period_gbp") {
    return data.tax_ebitda / data.fx_rate;
  }
};

export const addDataToExcel = (data, excelData, keys) => {
  keys.forEach((key) => {
    let value;

    if (calculationKeys.includes(key)) {
      value = handleNaNs(calculateMetric(key, data));
    } else {
      const modifiedKey = keyExceptionsMap[key] ?? key.replace("AP", "");
      value = data[modifiedKey]?.value ?? data[modifiedKey] ?? 0;
    }

    excelData[key].push(value);
  });
};

export const addYesOrNo = (data, excelData, keys) => {
  keys.forEach((key) => {
    const value = data[key] === true ? "Yes" : data[key] === false ? "No" : "-";
    excelData[key].push(value);
  });
};

export const addYesOrNoCIRCalcs = (data, excelData, keys) => {
  keys.forEach((key) => {
    const usedData = data[keyExceptionsMap[key]] ?? data[key];
    const value =
      usedData === true ? "Yes" : usedData === false ? "No" : (usedData ?? 0);
    excelData[key].push(value);
  });
};

export const setupExcelAccountingPeriodsData = (excelData) => {
  const selectedPeriodOfAccount = getSelectedPeriod();

  const relevant_caps = get_relevant_periods(
    selectedPeriodOfAccount.period_start,
    selectedPeriodOfAccount.period_end,
    getGroupData(),
  )?.sort((company1, company2) =>
    company1?.company_name?.localeCompare(company2?.company_name),
  );

  relevant_caps?.forEach((cap) => {
    excelData.apCompNames.push(cap.company_name);
    excelData.apStartDates.push(formatDate(cap.start_date));
    excelData.apEndDates.push(formatDate(cap.end_date));
    excelData.apFx.push(cap.fx_rate);
    excelData.apCurrency.push(cap.currency);

    addDataToExcel(cap, excelData, AP_potentialVariables);
  });
};

export const setupExcelAdjustedCapsData = (excelData, companies) => {
  const savedData = getGroupData();
  const selectedPeriodOfAccount = getSelectedPeriod();

  selectedPeriodOfAccount?.adjusted_caps?.forEach((cap) => {
    companies.push(cap.company_name);
    excelData.compNames.push(cap.company_name);
    excelData.currency.push(cap.currency);
    excelData.APSD.push(formatDate(cap.start_date));
    excelData.APED.push(formatDate(cap.end_date));
    excelData.regarded_period.push(cap.inclusion_period_length);
    excelData.disregarded_percent.push(cap.inclusion_factor);
    excelData.fx.push(cap.fx_rate);

    const selectedCompany = savedData?.companies?.find(
      (comp) => comp.company_ID === cap.company_ID,
    );
    excelData.date_joined.push(formatDate(selectedCompany?.date_join) ?? "N/A");
    excelData.date_lefted.push(formatDate(selectedCompany?.date_left) ?? "N/A");

    const potentialVariablesArray = [
      ntlr_potentialVariables,
      tlr_potentialVariables,
      ntdc_potentialVariables,
      tdc_potentialVariables,
      ofa_potentialVariables,
      cte_potentialVariables,
      adjustments_potentialVariables,
      reliefs_potentialVariables,
    ];

    potentialVariablesArray.forEach((variables) => {
      addDataToExcel(cap, excelData, variables);
    });

    // TO ASK the business team for the reason of this loop
    // ISSUES: iterates once and breaks + uses wrong metric company_id instead of company_ID

    // const company_accounting_periods = savedData.company_accounting_periods;
    // for (let c = 0; c < company_accounting_periods.length; c++) {
    //   if(company_accounting_periods[c].company_id === cap.company_id){

    //     let calculate_ebitda = (cap?.tax_ebitda?.value ?? 0) - (company_accounting_periods[c].tax_ebitda ??  0);
    //     excelData.tax_ebitda_adjustments.push(calculate_ebitda) // [TODO] - put this in.
    //   }

    //   break;
    // }

    const yesOrNoVariables = [
      cir_potentialVariables,
      company_potentialVariables,
    ];

    yesOrNoVariables.forEach((variables) => {
      addYesOrNo(cap, excelData, variables);
    });
  });
};

export const addSheetStyle = (worksheet, styleConfig) => {
  const rowFontSizes = styleConfig.rowFontSizes;
  if (rowFontSizes) {
    const rows = Object.keys(rowFontSizes);
    rows.forEach((rowNum) => {
      let row = worksheet.getRow(Number(rowNum));
      row.font = { ...row.font, size: rowFontSizes[rowNum] };
    });
  }

  if (styleConfig.boldRows) {
    styleConfig.boldRows.forEach(
      (row) =>
        (worksheet.getRow(row).font = {
          ...worksheet.getRow(row).font,
          bold: true,
        }),
    );
  }

  if (styleConfig.boldCells) {
    styleConfig.boldCells.forEach(
      (cell) =>
        (worksheet.getCell(cell).font = {
          ...worksheet.getCell(cell).font,
          bold: true,
        }),
    );
  }

  if (styleConfig.redTextCells) {
    styleConfig.redTextCells.forEach(
      (cell) =>
        (worksheet.getCell(cell).font = {
          ...worksheet.getCell(cell).font,
          color: { argb: "FFFF0000" },
        }),
    );
  }

  if (styleConfig.underlineBoldRows) {
    styleConfig.underlineBoldRows.forEach(
      (row) => (worksheet.getRow(row).font = { underline: true, bold: true }),
    );
  }

  if (styleConfig.italicRows) {
    styleConfig.italicRows.forEach(
      (row) => (worksheet.getRow(row).font = { italic: true }),
    );
  }

  if (styleConfig.italicCells) {
    styleConfig.italicCells.forEach(
      (cell) => (worksheet.getCell(cell).font = { italic: true }),
    );
  }

  if (styleConfig.numberFormatRows) {
    styleConfig.numberFormatRows.forEach(
      (row) => (worksheet.getRow(row).numFmt = "#,##0;(#,##0)"),
    );
  }

  if (styleConfig.numberFormatColumns) {
    styleConfig.numberFormatColumns.forEach(
      (col) => (worksheet.getColumn(col).numFmt = "#,##0;(#,##0)"),
    );
  }

  if (styleConfig.numberFormatCells) {
    styleConfig.numberFormatCells.forEach(
      (cell) => (worksheet.getCell(cell).numFmt = "#,##0;(#,##0)"),
    );
  }

  const colWidths = styleConfig.columnWidths;
  if (colWidths) {
    const cols = Object.keys(colWidths);
    cols.forEach(
      (col) => (worksheet.getColumn(Number(col)).width = colWidths[col]),
    );
  }

  if (styleConfig.rowHeights) {
    Object.keys(styleConfig.rowHeights).forEach((row) => {
      worksheet.getRow(Number(row)).height = styleConfig.rowHeights[row];
    });
  }

  if (styleConfig.wrapText) {
    styleConfig.wrapText.forEach(
      (col) => (worksheet.getColumn(col).alignment = { wrapText: true }),
    );
  }

  if (styleConfig.wrapTextCells) {
    styleConfig.wrapTextCells.forEach(
      (cell) => (worksheet.getCell(cell).alignment = { wrapText: true }),
    );
  }

  if (styleConfig.noWrapTextCells) {
    styleConfig.noWrapTextCells.forEach(
      (cell) => (worksheet.getCell(cell).alignment = { wrapText: false }),
    );
  }

  if (styleConfig.percentageFormat) {
    styleConfig.percentageFormat.forEach(
      (row) => (worksheet.getRow(row).numFmt = "0%"),
    );
  }

  if (styleConfig.percentageDecimalFormat) {
    styleConfig.percentageDecimalFormat.forEach(
      (row) => (worksheet.getRow(row).numFmt = "0.00%"),
    );
  }

  if (styleConfig.percentageFormatCells) {
    styleConfig.percentageFormatCells.forEach(
      (cell) => (worksheet.getCell(cell).numFmt = "0.00%"),
    );
  }

  if (styleConfig.horizontalRightAlignmentCols) {
    styleConfig.horizontalRightAlignmentCols.forEach(
      (col) => (worksheet.getColumn(col).alignment = { horizontal: "right" }),
    );
  }

  if (styleConfig.leftAlignedRows) {
    styleConfig.leftAlignedRows.forEach(
      (row) => (worksheet.getRow(row).alignment = { horizontal: "left" }),
    );
  }
};

export const styleDynamicAPColumns = (worksheet, length) => {
  for (let i = 0; i < length; i++) {
    worksheet.getColumn(i + 2).width = 15;
    worksheet.getColumn(i + 2).alignment = { horizontal: "right" };
  }
};

export const adjustHeadersAlignment = (worksheet, companyNames) => {
  const headerRow = worksheet.getRow(1);
  headerRow.height = 60;
  headerRow.alignment = { wrapText: true, vertical: "bottom" };

  worksheet.columns.slice(1).forEach((column, index) => {
    worksheet.getColumn(index + 2).width = 15;
  });
};

export const styleDynamicGroupSummaryColumns = (
  worksheet,
  ukCompaniesEndRow,
) => {
  for (let i = 0; i < 4; i++) {
    worksheet.getColumn(i + 3).width = 40;
  }

  addSquareBorder(worksheet, "B2", "C20", "B2", "C20", "medium");
  addSquareBorder(
    worksheet,
    "B23",
    `F${ukCompaniesEndRow}`,
    "B23",
    `F${ukCompaniesEndRow}`,
    "medium",
  );

  mergeSheetCells(worksheet, ["B2:C2"]);
  fixBorders(worksheet, ["B2"]);
};

export const getJsonFileName = () => {
  const selectedPeriod = getSelectedPeriod();
  const jsonFileName =
    getGroupName() +
    " - Summary output - " +
    formatDate(selectedPeriod?.period_start) +
    " - " +
    formatDate(selectedPeriod?.period_end);

  return jsonFileName;
};

export const mergeSheetCells = (worksheet, merges) => {
  merges.forEach((merge) => {
    worksheet.mergeCells(merge);
  });
};

export const alignCellsHorizontalCenter = (worksheet, cells) => {
  cells.forEach((cell) => {
    worksheet.getCell(cell).alignment = { horizontal: "center" };
  });
};

export const colourCells = (worksheet, cells, colour) => {
  cells.forEach((cell) => {
    worksheet.getCell(cell).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: colour },
    };
  });
};

export const addNumberFormatToColumns = (
  worksheet,
  columns,
  startRow,
  numOfRows,
) => {
  for (let i = 0; i < numOfRows; i++) {
    const row = startRow + i;

    columns.forEach((col) => {
      worksheet.getCell(`${col}${row}`).numFmt = "#,##0";
    });
  }
};

const fixBorders = (worksheet, cells) => {
  cells.forEach((cell) => {
    worksheet.getCell(cell).border = {
      ...worksheet.getCell(cell).border,
      right: { style: "medium" },
    };
  });
};

export const styleAllocatedRestrictionsColumns = (worksheet) => {
  addSquareBorder(worksheet, "B2", "E302", "B2", "E302", "medium");
};

export const styleAllocatedReactivationColumns = (worksheet) => {
  addSquareBorder(worksheet, "B2", "E302", "B2", "E302", "medium");
};
